import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  List,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Grid,
  Slider,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from '@mui/material';
import './MyRoutesPage.css';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  SwapVert as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  StraightenOutlined as DistanceIcon,
  Terrain as ElevationIcon,
  AccessTime as DurationIcon,
  Upload as UploadIcon,
  Share as ShareIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCyclingToasts } from '../hooks/useCyclingToasts';
import { RouteStorageManager, StorageType } from '../services/storage';
import { RouteListItem } from '../components/routes/RouteListItem';
import { RenameRouteModal } from '../components/modals/RenameRouteModal';
import LoadRouteConfirmationModal from '../components/modals/LoadRouteConfirmationModal';
import { ThumbnailPreviewModal } from '../components/modals/ThumbnailPreviewModal';
import { ElevationPreviewModal } from '../components/modals/ElevationPreviewModal';
import DownloadModal from '../components/modals/DownloadModal';
import { GPXUploadModal } from '../components/modals/GPXUploadModal';
import { PlanningSessionManager } from '../utils/planningSessionStorage';
import { PageHeader } from '../components/common/PageHeader';
import { getRouteThumbnailUrl } from '../utils/thumbnailGenerator';
import { downloadRouteAsGPX } from '../utils/routeFullDataUtils';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector';
import type { Route } from '../types/storage';
import TrekkingIcon from '../assets/bike_profile_icons/BikeProfile_Trekking_light.svg';
import RoadBikeIcon from '../assets/bike_profile_icons/BikeProfile_RoadBike_light.svg';
import GravelBikeIcon from '../assets/bike_profile_icons/BikeProfile_GravelBike_light.svg';
import MountainBikeIcon from '../assets/bike_profile_icons/BikeProfile_MountainBike_light.svg';

// Lazy loading configuration
const ITEMS_PER_PAGE = 20; // Load 20 routes at a time

// Helper function to get bike profile icon for chips
const getBikeProfileChipIcon = (profile: string) => {
  switch (profile) {
    case 'road':
      return <img src={RoadBikeIcon} alt="Road Bike" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    case 'mtb':
      return <img src={MountainBikeIcon} alt="Mountain Bike" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    case 'gravel':
      return <img src={GravelBikeIcon} alt="Gravel Bike" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    case 'trekking':
      return <img src={TrekkingIcon} alt="Trekking" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    case 'fastbike':
      return <img src={RoadBikeIcon} alt="Road Bike" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    case 'bike': // Fallback for old "bike" entries, treat as trekking
      return <img src={TrekkingIcon} alt="Trekking" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
    default:
      return <img src={TrekkingIcon} alt="Bike" style={{ height: 16, width: 16, aspectRatio: 'auto' }} />;
  }
};

const MyRoutesPage: React.FC = () => {
  const { user, getAccessToken, refreshSession, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const cyclingToasts = useCyclingToasts();
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Load route confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingRouteToLoad, setPendingRouteToLoad] = useState<Route | null>(null);
  const [currentSessionForModal, setCurrentSessionForModal] = useState<{
    pointCount: number;
    routeName: string | null;
    hasUnsavedChanges: boolean;
    routeMetrics?: {
      distance: number | string;
      duration: number | string;
      elevationGain: number | string;
    } | null;
  } | null>(null);
  
  // Thumbnail preview modal state
  const [thumbnailPreviewOpen, setThumbnailPreviewOpen] = useState(false);
  const [thumbnailPreviewRoute, setThumbnailPreviewRoute] = useState<Route | null>(null);
  
  // Elevation preview modal state
  const [elevationPreviewOpen, setElevationPreviewOpen] = useState(false);
  const [elevationPreviewRoute, setElevationPreviewRoute] = useState<Route | null>(null);
  
  // GPX Upload state
  const [gpxUploadModalOpen, setGpxUploadModalOpen] = useState(false);
  
  // Search filter state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Advanced filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [distanceRange, setDistanceRange] = useState<[number, number]>([0, 100]); // km
  const [elevationRange, setElevationRange] = useState<[number, number]>([0, 2000]); // meters
  const [durationRange, setDurationRange] = useState<[number, number]>([0, 480]); // minutes
  const [selectedBikeProfiles, setSelectedBikeProfiles] = useState<string[]>([]);
  
  // Sort state
  const [sortBy, setSortBy] = useState<'createdAt' | 'name' | 'distance' | 'duration' | 'elevation'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  
  // Get unique bike profiles from user's actual routes
  const availableBikeProfiles = useMemo(() => {
    const uniqueProfiles = new Set<string>();
    routes.forEach(route => {
      if (route.bikeProfile) {
        uniqueProfiles.add(route.bikeProfile);
      }
    });
    return Array.from(uniqueProfiles).sort();
  }, [routes]);
  
  // Lazy loading state
  const [displayedRoutes, setDisplayedRoutes] = useState<Route[]>([]);
  const [hasMoreRoutes, setHasMoreRoutes] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);

  // Initialize storage manager - memoized to prevent re-creation on every render
  const storageManager = useMemo(() => new RouteStorageManager({
    type: StorageType.SUPABASE,
    endpoint: import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000' // Supabase Kong gateway
  }, {
    getAccessToken,
    refreshSession
  }), [getAccessToken, refreshSession]);

  // Filter and sort routes based on search query, advanced filters, and sort criteria
  const filteredRoutes = useMemo(() => {
    let filtered = routes;
    
    // Apply text search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(route => 
        route.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply distance filter (convert meters to km)
    filtered = filtered.filter(route => {
      const distanceKm = (route.distance || 0) / 1000;
      return distanceKm >= distanceRange[0] && distanceKm <= distanceRange[1];
    });
    
    // Apply elevation filter
    filtered = filtered.filter(route => {
      const elevation = route.ascend || 0;
      return elevation >= elevationRange[0] && elevation <= elevationRange[1];
    });
    
    // Apply duration filter (convert seconds to minutes)
    filtered = filtered.filter(route => {
      const durationMinutes = (route.duration || 0) / 60;
      return durationMinutes >= durationRange[0] && durationMinutes <= durationRange[1];
    });
    
    // Apply bike profile filter
    if (selectedBikeProfiles.length > 0) {
      filtered = filtered.filter(route => 
        selectedBikeProfiles.includes(route.bikeProfile)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'distance':
          comparison = (a.distance || 0) - (b.distance || 0);
          break;
        case 'duration':
          comparison = (a.duration || 0) - (b.duration || 0);
          break;
        case 'elevation':
          comparison = (a.ascend || 0) - (b.ascend || 0);
          break;
        case 'createdAt':
        default:
          // Parse date strings and sort
          const dateA = new Date(a.created || 0).getTime();
          const dateB = new Date(b.created || 0).getTime();
          comparison = dateA - dateB;
          break;
      }
      
      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [routes, searchQuery, distanceRange, elevationRange, durationRange, selectedBikeProfiles, sortBy, sortDirection]);

  // Calculate dynamic ranges based on actual data
  const dataRanges = useMemo(() => {
    if (routes.length === 0) {
      return {
        distance: [0, 100] as [number, number],
        elevation: [0, 2000] as [number, number],
        duration: [0, 480] as [number, number]
      };
    }
    
    const distances = routes.map(r => (r.distance || 0) / 1000); // Convert to km
    const elevations = routes.map(r => r.ascend || 0);
    const durations = routes.map(r => (r.duration || 0) / 60); // Convert to minutes
    
    return {
      distance: [0, Math.ceil(Math.max(...distances) * 1.1)] as [number, number],
      elevation: [0, Math.ceil(Math.max(...elevations) * 1.1)] as [number, number],
      duration: [0, Math.ceil(Math.max(...durations) * 1.1)] as [number, number]
    };
  }, [routes]);

  // Update filter ranges when data changes
  useEffect(() => {
    setDistanceRange([0, dataRanges.distance[1]]);
    setElevationRange([0, dataRanges.elevation[1]]);
    setDurationRange([0, dataRanges.duration[1]]);
  }, [dataRanges]);

  // Check if any advanced filters are active
  const hasActiveFilters = useMemo(() => {
    return distanceRange[0] > 0 || distanceRange[1] < dataRanges.distance[1] ||
           elevationRange[0] > 0 || elevationRange[1] < dataRanges.elevation[1] ||
           durationRange[0] > 0 || durationRange[1] < dataRanges.duration[1] ||
           selectedBikeProfiles.length > 0;
  }, [distanceRange, elevationRange, durationRange, selectedBikeProfiles, dataRanges]);

  // Handle bike profile toggle
  const handleBikeProfileToggle = (profile: string) => {
    setSelectedBikeProfiles(prev => 
      prev.includes(profile)
        ? prev.filter(p => p !== profile)
        : [...prev, profile]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setDistanceRange([0, dataRanges.distance[1]]);
    setElevationRange([0, dataRanges.elevation[1]]);
    setDurationRange([0, dataRanges.duration[1]]);
    setSelectedBikeProfiles([]);
  };

  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (sortOption: 'createdAt' | 'name' | 'distance' | 'duration' | 'elevation', direction: 'asc' | 'desc') => {
    setSortBy(sortOption);
    setSortDirection(direction);
    setSortAnchorEl(null);
  };

  const getSortLabel = () => {
    const directionLabel = sortDirection === 'asc' ? '↑' : '↓';
    switch (sortBy) {
      case 'name': return `Name ${directionLabel}`;
      case 'distance': return `Distance ${directionLabel}`;
      case 'duration': return `Duration ${directionLabel}`;
      case 'elevation': return `Elevation ${directionLabel}`;
      case 'createdAt':
      default: return `Date ${directionLabel}`;
    }
  };

  useEffect(() => {
    // Don't redirect while auth is loading
    if (authLoading) {
      return;
    }
    
    if (!user) {
      navigate('/');
      return;
    }
    
    loadRoutes();
  }, [user, navigate, authLoading]);

  // Clean up selected bike profiles when available profiles change
  useEffect(() => {
    setSelectedBikeProfiles(prev => 
      prev.filter(profile => availableBikeProfiles.includes(profile))
    );
  }, [availableBikeProfiles]);

  // Update displayed routes based on filtered routes
  const updateDisplayedRoutes = useCallback((routesToDisplay: Route[]) => {
    const initialRoutes = routesToDisplay.slice(0, ITEMS_PER_PAGE);
    setDisplayedRoutes(initialRoutes);
    setHasMoreRoutes(routesToDisplay.length > ITEMS_PER_PAGE);
  }, []);

  // Update displayed routes when filtered routes change
  useEffect(() => {
    updateDisplayedRoutes(filteredRoutes);
  }, [filteredRoutes, updateDisplayedRoutes]);

  const loadRoutes = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await storageManager.getRoutes(user.id);
      if (result.success && result.data) {
        setRoutes(result.data);
        // Reset displayed routes when routes change (e.g., after loading or filtering)
        updateDisplayedRoutes(result.data);
      } else {
        // Check for authentication errors
        const errorMessage = result.error || 'Failed to load routes';
        if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('auth')) {
          console.error('Authentication error loading routes, redirecting to login');
          navigate('/');
          return;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load routes';
      
      // Check if it's an authentication error
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('auth')) {
        navigate('/');
        return;
      }
      
      setError('Failed to load routes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load more routes function
  const loadMoreRoutes = useCallback(() => {
    if (loadingMore || !hasMoreRoutes) return;
    
    setLoadingMore(true);
    
    // Simulate async loading (in case we want to add server-side pagination later)
    setTimeout(() => {
      const currentLength = displayedRoutes.length;
      const nextRoutes = filteredRoutes.slice(currentLength, currentLength + ITEMS_PER_PAGE);
      
      setDisplayedRoutes(prev => [...prev, ...nextRoutes]);
      setHasMoreRoutes(currentLength + nextRoutes.length < filteredRoutes.length);
      setLoadingMore(false);
    }, 300); // Small delay to show loading state
  }, [displayedRoutes.length, filteredRoutes, loadingMore, hasMoreRoutes]);

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreRoutes && !loadingMore) {
          loadMoreRoutes();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMoreRoutes, loadingMore, loadMoreRoutes]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, route: Route) => {
    setAnchorEl(event.currentTarget);
    setSelectedRoute(route);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRoute(null);
  };

  const handleRenameRoute = () => {
    setRenameModalOpen(true);
    setAnchorEl(null); // Close menu but keep selectedRoute
  };

  const handleRenameSubmit = async (routeId: string, newName: string) => {
    try {
      const result = await storageManager.updateRoute(routeId, { name: newName });
      if (result.success) {
        await loadRoutes(); // Reload the routes list
      } else {
        throw new Error(result.error || 'Failed to rename route');
      }
    } catch (err) {
      setError('Failed to rename route. Please try again.');
      throw err; // Re-throw so modal can handle the error
    }
  };

  const handleRenameModalClose = () => {
    setRenameModalOpen(false);
    setSelectedRoute(null);
  };

  const handleShareRoute = () => {
    setShareModalOpen(true);
    setAnchorEl(null); // Close menu but keep selectedRoute
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedRoute(null);
  };

  const handleLoadRoute = (route: Route) => {
    // Check if there's an existing planning session with unsaved changes
    const existingSession = PlanningSessionManager.loadPlanningSession();
    const hasActiveSession = existingSession && 
      existingSession.points && 
      existingSession.points.length > 0;
    const hasUnsavedChanges = existingSession?.hasUnsavedChanges || false;

    if (hasActiveSession && hasUnsavedChanges) {
      // Store the current session information for the modal
      setCurrentSessionForModal({
        pointCount: existingSession.points.length,
        routeName: existingSession.currentRouteName,
        hasUnsavedChanges: existingSession.hasUnsavedChanges || false,
        routeMetrics: existingSession.routeMetrics || null
      });

      // Show confirmation modal
      setPendingRouteToLoad(route);
      setConfirmationModalOpen(true);
    } else {
      // No active session or no unsaved changes, load directly
      loadRouteDirectly(route);
    }
  };

  // Function to handle thumbnail click
  const handleThumbnailClick = (route: Route, type?: 'route' | 'elevation') => {
    if (type === 'elevation') {
      setElevationPreviewRoute(route);
      setElevationPreviewOpen(true);
    } else {
      setThumbnailPreviewRoute(route);
      setThumbnailPreviewOpen(true);
    }
  };

  const handleThumbnailPreviewClose = () => {
    setThumbnailPreviewOpen(false);
    setThumbnailPreviewRoute(null);
  };

  const handleElevationPreviewClose = () => {
    setElevationPreviewOpen(false);
    setElevationPreviewRoute(null);
  };

  // Function to load route directly (extracted for reuse)
  const loadRouteDirectly = (route: Route) => {
    // Navigate directly to the route path instead of using sessionStorage
    // This triggers URL-based route loading with proper loading states
    navigate(`/route/${route.id}`);
  };

  // Function to handle confirmation modal actions
  const handleConfirmLoadRoute = () => {
    if (pendingRouteToLoad) {
      loadRouteDirectly(pendingRouteToLoad);
      setPendingRouteToLoad(null);
      setCurrentSessionForModal(null);
      setConfirmationModalOpen(false);
    }
  };

  const handleCancelLoadRoute = () => {
    setPendingRouteToLoad(null);
    setCurrentSessionForModal(null);
    setConfirmationModalOpen(false);
  };

  const handleDownloadRoute = async () => {
    if (selectedRoute) {
      try {
        // Convert route points to positions for API call
        const points = selectedRoute.points.sort((a, b) => a.orderIndex - b.orderIndex);
        const positions = points.map(point => ({
          lat: point.lat,
          lng: point.lng
        }));
        
        // Use optimized GPX download with stored full data when available
        await downloadRouteAsGPX(
          selectedRoute.id || null, // Pass route ID for optimization
          positions, 
          selectedRoute.bikeProfile as RouteProfile, 
          selectedRoute.name,
          () => setDownloadModalOpen(true),   // onLoadingStart
          () => setDownloadModalOpen(false),   // onLoadingEnd
          { getAccessToken, refreshSession }   // sessionFunctions
        );
      } catch (error) {
        setDownloadModalOpen(false); // Ensure modal is closed on error
        // Fallback to simple waypoint-based GPX if API fails
        const points = selectedRoute.points.sort((a, b) => a.orderIndex - b.orderIndex);
        
        const gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="CyclingBuddy" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${selectedRoute.name}</name>
    <trkseg>
${points.map(point => `      <trkpt lat="${point.lat}" lon="${point.lng}"></trkpt>`).join('\n')}
    </trkseg>
  </trk>
</gpx>`;

        const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedRoute.name.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    }
    handleMenuClose();
  };

  const handleDeleteRoute = async () => {
    setDeleteModalOpen(true);
    setAnchorEl(null); // Close menu but keep selectedRoute
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRoute?.id) return;
    
    const routeName = selectedRoute.name; // Store name before clearing selectedRoute
    
    try {
      const result = await storageManager.deleteRoute(selectedRoute.id);
      if (result.success) {
        cyclingToasts.routeDeleted(routeName);
        await loadRoutes(); // Reload the routes list
      }
    } catch (err) {
      setError('Failed to delete route. Please try again.');
    }
    setDeleteModalOpen(false);
    setSelectedRoute(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedRoute(null);
  };

  const handleRouteUploaded = async (routeId: string) => {
    // Refresh the routes list to include the newly uploaded route
    await loadRoutes();
    
    // Close the modal
    setGpxUploadModalOpen(false);
    
    // Navigate directly to the route path instead of using sessionStorage
    // This triggers URL-based route loading with proper loading states
    navigate(`/route/${routeId}`);
  };

  // Show loading spinner while authentication is being checked
  if (authLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box>
        {/* Header with back button and title */}
        <PageHeader 
          title="My Routes" 
          rightAction={
            <IconButton
              onClick={() => setGpxUploadModalOpen(true)}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
              aria-label="Upload GPX route"
            >
              <UploadIcon />
            </IconButton>
          }
        />
        
        {/* Search and Filter Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 3,
            alignItems: 'center'
          }}
        >
          <TextField
            placeholder="Search routes by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ 
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: '1.2rem' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <IconButton
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{ 
              border: 1,
              borderColor: hasActiveFilters ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 1,
              backgroundColor: hasActiveFilters ? 'primary.50' : 'transparent',
              color: hasActiveFilters ? 'primary.main' : 'action.active',
              '&:hover': {
                backgroundColor: hasActiveFilters ? 'primary.100' : 'action.hover'
              }
            }}
            title={filtersOpen ? "Hide filters" : "Show filters"}
          >
            {filtersOpen ? <ExpandLessIcon /> : <FilterListIcon />}
          </IconButton>
          <IconButton
            onClick={handleSortClick}
            sx={{ 
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              backgroundColor: 'transparent',
              color: 'action.active',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
            title={`Sort by: ${getSortLabel()}`}
          >
            <SortIcon />
          </IconButton>
        </Box>
        
        {/* Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* Date */}
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 180, px: 2 }}>
            <span 
              style={{ 
                fontWeight: sortBy === 'createdAt' ? 'bold' : 'normal',
                color: sortBy === 'createdAt' ? '#4a7c2a' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (sortBy === 'createdAt') {
                  // Toggle direction if already sorting by this category
                  handleSortSelect('createdAt', sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  // Default to descending for new category
                  handleSortSelect('createdAt', 'desc');
                }
              }}
            >
              Date
            </span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '44px' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleSortSelect('createdAt', 'desc'); }}
                  sx={{ 
                    p: 0.5,
                    height: '32px',
                    width: '32px',
                    color: sortBy === 'createdAt' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                    '& .MuiSvgIcon-root': {
                      fontWeight: sortBy === 'createdAt' && sortDirection === 'desc' ? 900 : 400,
                      fontSize: sortBy === 'createdAt' && sortDirection === 'desc' ? '1.2rem' : '1rem'
                    }
                  }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem', 
                    lineHeight: 1,
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    color: sortBy === 'createdAt' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                    fontWeight: sortBy === 'createdAt' && sortDirection === 'desc' ? 'bold' : 'normal'
                  }}
                >
                  Newest
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '44px' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleSortSelect('createdAt', 'asc'); }}
                  sx={{ 
                    p: 0.5,
                    height: '32px',
                    width: '32px',
                    color: sortBy === 'createdAt' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                    '& .MuiSvgIcon-root': {
                      fontWeight: sortBy === 'createdAt' && sortDirection === 'asc' ? 900 : 400,
                      fontSize: sortBy === 'createdAt' && sortDirection === 'asc' ? '1.2rem' : '1rem'
                    }
                  }}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem', 
                    lineHeight: 1,
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    color: sortBy === 'createdAt' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                    fontWeight: sortBy === 'createdAt' && sortDirection === 'asc' ? 'bold' : 'normal'
                  }}
                >
                  Oldest
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          {/* Name */}
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 180, px: 2 }}>
            <span 
              style={{ 
                fontWeight: sortBy === 'name' ? 'bold' : 'normal',
                color: sortBy === 'name' ? '#4a7c2a' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (sortBy === 'name') {
                  // Toggle direction if already sorting by this category
                  handleSortSelect('name', sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  // Default to ascending for name (A-Z makes more sense)
                  handleSortSelect('name', 'asc');
                }
              }}
            >
              Name
            </span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '44px' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleSortSelect('name', 'asc'); }}
                  sx={{ 
                    p: 0.5,
                    height: '32px',
                    width: '32px',
                    color: sortBy === 'name' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                    '& .MuiSvgIcon-root': {
                      fontWeight: sortBy === 'name' && sortDirection === 'asc' ? 900 : 400,
                      fontSize: sortBy === 'name' && sortDirection === 'asc' ? '1.2rem' : '1rem'
                    }
                  }}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem', 
                    lineHeight: 1,
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    color: sortBy === 'name' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                    fontWeight: sortBy === 'name' && sortDirection === 'asc' ? 'bold' : 'normal'
                  }}
                >
                  A-Z
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '44px' }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); handleSortSelect('name', 'desc'); }}
                  sx={{ 
                    p: 0.5,
                    height: '32px',
                    width: '32px',
                    color: sortBy === 'name' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                    '& .MuiSvgIcon-root': {
                      fontWeight: sortBy === 'name' && sortDirection === 'desc' ? 900 : 400,
                      fontSize: sortBy === 'name' && sortDirection === 'desc' ? '1.2rem' : '1rem'
                    }
                  }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.6rem', 
                    lineHeight: 1,
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    color: sortBy === 'name' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                    fontWeight: sortBy === 'name' && sortDirection === 'desc' ? 'bold' : 'normal'
                  }}
                >
                  Z-A
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          {/* Distance */}
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 180, px: 2 }}>
            <span 
              style={{ 
                fontWeight: sortBy === 'distance' ? 'bold' : 'normal',
                color: sortBy === 'distance' ? '#4a7c2a' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (sortBy === 'distance') {
                  // Toggle direction if already sorting by this category
                  handleSortSelect('distance', sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  // Default to descending for distance (longest first)
                  handleSortSelect('distance', 'desc');
                }
              }}
            >
              Distance
            </span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('distance', 'desc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'distance' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'distance' && sortDirection === 'desc' ? 900 : 400,
                    fontSize: sortBy === 'distance' && sortDirection === 'desc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('distance', 'asc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'distance' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'distance' && sortDirection === 'asc' ? 900 : 400,
                    fontSize: sortBy === 'distance' && sortDirection === 'asc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          {/* Duration */}
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 180, px: 2 }}>
            <span 
              style={{ 
                fontWeight: sortBy === 'duration' ? 'bold' : 'normal',
                color: sortBy === 'duration' ? '#4a7c2a' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (sortBy === 'duration') {
                  // Toggle direction if already sorting by this category
                  handleSortSelect('duration', sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  // Default to descending for duration (longest first)
                  handleSortSelect('duration', 'desc');
                }
              }}
            >
              Duration
            </span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('duration', 'desc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'duration' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'duration' && sortDirection === 'desc' ? 900 : 400,
                    fontSize: sortBy === 'duration' && sortDirection === 'desc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('duration', 'asc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'duration' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'duration' && sortDirection === 'asc' ? 900 : 400,
                    fontSize: sortBy === 'duration' && sortDirection === 'asc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          {/* Elevation */}
          <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 180, px: 2 }}>
            <span 
              style={{ 
                fontWeight: sortBy === 'elevation' ? 'bold' : 'normal',
                color: sortBy === 'elevation' ? '#4a7c2a' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (sortBy === 'elevation') {
                  // Toggle direction if already sorting by this category
                  handleSortSelect('elevation', sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  // Default to descending for elevation (highest first)
                  handleSortSelect('elevation', 'desc');
                }
              }}
            >
              Elevation
            </span>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('elevation', 'desc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'elevation' && sortDirection === 'desc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'elevation' && sortDirection === 'desc' ? 900 : 400,
                    fontSize: sortBy === 'elevation' && sortDirection === 'desc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={(e) => { e.stopPropagation(); handleSortSelect('elevation', 'asc'); }}
                sx={{ 
                  p: 0.5,
                  color: sortBy === 'elevation' && sortDirection === 'asc' ? '#4a7c2a' : '#bdbdbd',
                  '& .MuiSvgIcon-root': {
                    fontWeight: sortBy === 'elevation' && sortDirection === 'asc' ? 900 : 400,
                    fontSize: sortBy === 'elevation' && sortDirection === 'asc' ? '1.2rem' : '1rem'
                  }
                }}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
        </Menu>
        
        {/* Bike Profile Filter - Only show if there are profiles */}
        {availableBikeProfiles.length > 0 && (
          <Box sx={{ mb: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', minWidth: 'fit-content' }}>
                Bike Profile:
              </Typography>
              {availableBikeProfiles.map((profile) => (
                <Chip
                  key={profile}
                  icon={getBikeProfileChipIcon(profile)}
                  label={profile.charAt(0).toUpperCase() + profile.slice(1)}
                  onClick={() => handleBikeProfileToggle(profile)}
                  color={selectedBikeProfiles.includes(profile) ? "primary" : "default"}
                  variant={selectedBikeProfiles.includes(profile) ? "filled" : "outlined"}
                  size="small"
                  sx={{ 
                    height: 26, 
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': {
                      marginLeft: '4px',
                      marginRight: '-2px'
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
        
        {/* Advanced Filters Panel */}
        <Collapse in={filtersOpen}>
          <Paper 
            sx={{ 
              px: 2,
              pt: 1,
              pb: 2, 
              mb: 2, 
              backgroundColor: 'grey.50',
              border: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                Filter Routes
              </Typography>
              <Chip
                label="Clear All"
                size="small"
                onClick={clearAllFilters}
                onDelete={clearAllFilters}
                color="primary"
                variant="outlined"
                sx={{ 
                  height: 24,
                  visibility: hasActiveFilters ? 'visible' : 'hidden'
                }}
              />
            </Box>
            
            <Grid container spacing={1.5}>
              {/* Distance Filter */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <DistanceIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    Distance
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -0.5 }}>
                  <Typography variant="caption" color="text.secondary" className="filter-value-from">
                    {Math.round(distanceRange[0])} km
                  </Typography>
                  <Slider
                    value={distanceRange}
                    onChange={(_, newValue) => setDistanceRange(newValue as [number, number])}
                    valueLabelDisplay="auto"
                    min={0}
                    max={dataRanges.distance[1]}
                    step={1}
                    valueLabelFormat={(value) => `${Math.round(value)} km`}
                    size="small"
                    className="filter-slider"
                  />
                  <Typography variant="caption" color="text.secondary" className="filter-value-to">
                    {Math.round(distanceRange[1])} km
                  </Typography>
                </Box>
              </Grid>
              
              {/* Elevation Filter */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <ElevationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    Elevation Gain
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -0.5 }}>
                  <Typography variant="caption" color="text.secondary" className="filter-value-from">
                    {elevationRange[0]} m
                  </Typography>
                  <Slider
                    value={elevationRange}
                    onChange={(_, newValue) => setElevationRange(newValue as [number, number])}
                    valueLabelDisplay="auto"
                    min={0}
                    max={dataRanges.elevation[1]}
                    step={10}
                    valueLabelFormat={(value) => `${value} m`}
                    size="small"
                    className="filter-slider"
                  />
                  <Typography variant="caption" color="text.secondary" className="filter-value-to">
                    {elevationRange[1]} m
                  </Typography>
                </Box>
              </Grid>
              
              {/* Duration Filter */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <DurationIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    Duration
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -0.5 }}>
                  <Typography variant="caption" color="text.secondary" className="filter-value-from">
                    {(() => {
                      const hours = Math.floor(durationRange[0] / 60);
                      const minutes = Math.round(durationRange[0] % 60);
                      if (hours > 0 && minutes > 0) {
                        return `${hours}h ${minutes}min`;
                      } else if (hours > 0) {
                        return `${hours}h`;
                      } else {
                        return `${minutes}min`;
                      }
                    })()}
                  </Typography>
                  <Slider
                    value={durationRange}
                    onChange={(_, newValue) => setDurationRange(newValue as [number, number])}
                    valueLabelDisplay="auto"
                    min={0}
                    max={dataRanges.duration[1]}
                    step={5}
                    valueLabelFormat={(value) => {
                      const hours = Math.floor(value / 60);
                      const minutes = Math.round(value % 60);
                      if (hours > 0 && minutes > 0) {
                        return `${hours}h ${minutes}min`;
                      } else if (hours > 0) {
                        return `${hours}h`;
                      } else {
                        return `${minutes}min`;
                      }
                    }}
                    size="small"
                    className="filter-slider"
                  />
                  <Typography variant="caption" color="text.secondary" className="filter-value-to">
                    {(() => {
                      const hours = Math.floor(durationRange[1] / 60);
                      const minutes = Math.round(durationRange[1] % 60);
                      if (hours > 0 && minutes > 0) {
                        return `${hours}h ${minutes}min`;
                      } else if (hours > 0) {
                        return `${hours}h`;
                      } else {
                        return `${minutes}min`;
                      }
                    })()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
        
        {/* Route count - Always visible when routes are loaded */}
        {!loading && routes.length > 0 && (
          <Box sx={{ mb: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Showing {filteredRoutes.length} of {routes.length} routes
            </Typography>
            {hasActiveFilters && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="primary" sx={{ fontSize: '0.75rem' }}>
                  Filters applied
                </Typography>
                <IconButton
                  size="small"
                  onClick={clearAllFilters}
                  sx={{ 
                    p: 0.25,
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50'
                    }
                  }}
                  title="Clear all filters"
                >
                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
        
        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && routes.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
              You haven't saved any routes yet. Create a route on the map and save it to see it here.
            </Typography>
          </Paper>
        )}
        
        {!loading && !error && routes.length > 0 && filteredRoutes.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
              No routes found matching "{searchQuery}". Try adjusting your search terms.
            </Typography>
          </Paper>
        )}
        
        {!loading && filteredRoutes.length > 0 && (
          <Paper elevation={1}>
            <List disablePadding>
              {displayedRoutes.map((route, index) => (
                <RouteListItem
                  key={route.id}
                  route={route}
                  onLoadRoute={handleLoadRoute}
                  onMenuOpen={handleMenuOpen}
                  onThumbnailClick={handleThumbnailClick}
                  isLast={index === displayedRoutes.length - 1 && !hasMoreRoutes}
                />
              ))}
              
              {/* Loading more indicator */}
              {(loadingMore || hasMoreRoutes) && (
                <Box 
                  ref={observerRef}
                  display="flex" 
                  justifyContent="center" 
                  py={2}
                  sx={{ minHeight: 60 }}
                >
                  {loadingMore && <CircularProgress size={24} />}
                  {!loadingMore && hasMoreRoutes && (
                    <Typography variant="body2" color="text.secondary">
                      Scroll for more routes...
                    </Typography>
                  )}
                </Box>
              )}
            </List>
          </Paper>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleRenameRoute}>
          <EditIcon sx={{ mr: 1 }} />
          Rename Route
        </MenuItem>
        <MenuItem onClick={handleShareRoute}>
          <ShareIcon sx={{ mr: 1 }} />
          Share Route
        </MenuItem>
        <MenuItem onClick={handleDownloadRoute}>
          <DownloadIcon sx={{ mr: 1 }} />
          Download GPX
        </MenuItem>
        <MenuItem onClick={handleDeleteRoute} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Route
        </MenuItem>
      </Menu>

      <RenameRouteModal
        open={renameModalOpen}
        route={selectedRoute}
        onClose={handleRenameModalClose}
        onRename={handleRenameSubmit}
      />

      {/* Share Route Modal */}
      <Dialog 
        open={shareModalOpen} 
        onClose={handleShareModalClose}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus={true}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon color="primary" fontSize="small" />
              Share Route
            </Typography>
            <IconButton 
              onClick={handleShareModalClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3 }} color="text.secondary">
            Control who can view this route and get a shareable link.
          </Typography>
          
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} component="legend" sx={{ mb: 1 }}>Route Visibility</Typography>
            <RadioGroup
              value={selectedRoute?.isPublic ? 'public' : 'private'}
              onChange={async (event) => {
                const isPublic = event.target.value === 'public';
                if (selectedRoute && selectedRoute.isPublic !== isPublic) {
                  try {
                    const result = await storageManager.updateRoute(selectedRoute.id!, { isPublic });
                    if (result.success) {
                      await loadRoutes(); // Reload routes to reflect the change
                      // Update the selectedRoute state to reflect the new status
                      setSelectedRoute({ ...selectedRoute, isPublic });
                    } else {
                      throw new Error(result.error || `Failed to make route ${isPublic ? 'public' : 'private'}`);
                    }
                  } catch (err) {
                    setError(`Failed to make route ${isPublic ? 'public' : 'private'}. Please try again.`);
                  }
                }
              }}
              sx={{
                width: '100%',
                '& .MuiFormControlLabel-root': {
                  margin: '8px 0',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: '100%',
                  marginLeft: 0,
                  marginRight: 0,
                  boxSizing: 'border-box',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                },
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  width: '100%',
                  marginLeft: '8px'
                },
                '& .Mui-checked + .MuiFormControlLabel-label': {
                  fontWeight: 500
                }
              }}
            >
              <FormControlLabel 
                value="private" 
                control={<Radio size="small" />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <LockIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Private
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Only you can view
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel 
                value="public" 
                control={<Radio size="small" />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <LockOpenIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'success.main' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Public
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Anyone with link can view
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
          
          {selectedRoute?.isPublic && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Share this link:
              </Typography>
              <TextField
                fullWidth
                value={selectedRoute ? `${window.location.origin}/route/${selectedRoute.id}` : ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          if (selectedRoute && selectedRoute.isPublic) {
                            const url = `${window.location.origin}/route/${selectedRoute.id}`;
                            navigator.clipboard.writeText(url).then(() => {
                              cyclingToasts.copied('Share link');
                            }).catch(console.error);
                          }
                        }}
                        edge="end"
                      >
                        📋
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button 
            onClick={handleShareModalClose}
            variant="outlined"
          >
            Cancel
          </Button>
          {selectedRoute?.isPublic && (
            <Button 
              onClick={() => {
                if (selectedRoute) {
                  const url = `${window.location.origin}/route/${selectedRoute.id}`;
                  
                  // Check if native share API is available (mainly mobile)
                  const hasNativeShare = 'share' in navigator && typeof (navigator as any).share === 'function';
                  
                  if (hasNativeShare) {
                    (navigator as any).share({
                      title: `${selectedRoute.name} - CyclingBuddy Route`,
                      text: `Check out this cycling route: ${selectedRoute.name}`,
                      url: url
                    }).catch(console.error);
                  } else {
                    // Fallback: copy to clipboard
                    navigator.clipboard.writeText(url).then(() => {
                      cyclingToasts.copied('Share link');
                    }).catch(console.error);
                  }
                }
                handleShareModalClose();
              }}
              variant="contained"
            >
              {('share' in navigator && typeof (navigator as any).share === 'function') ? 'Share' : 'Copy Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disableRestoreFocus={true}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteIcon color="error" fontSize="small" />
              Delete Route
            </Typography>
            <IconButton 
              onClick={handleDeleteCancel}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" id="delete-dialog-description" color="text.secondary">
            Are you sure you want to delete "{selectedRoute?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel} 
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Route Confirmation Modal */}
      {currentSessionForModal && pendingRouteToLoad && (
        <LoadRouteConfirmationModal
          open={confirmationModalOpen}
          onClose={handleCancelLoadRoute}
          onConfirm={handleConfirmLoadRoute}
          routeName={pendingRouteToLoad.name}
          currentSessionInfo={currentSessionForModal}
        />
      )}

      {/* Thumbnail Preview Modal */}
      <ThumbnailPreviewModal
        open={thumbnailPreviewOpen}
        onClose={handleThumbnailPreviewClose}
        thumbnailUrl={thumbnailPreviewRoute?.thumbnail_path ? 
          getRouteThumbnailUrl(
            import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000',
            thumbnailPreviewRoute.thumbnail_path,
            'large'
          ) : null
        }
        routeName={thumbnailPreviewRoute?.name || 'Route'}
      />

      {/* Elevation Preview Modal */}
      <ElevationPreviewModal
        open={elevationPreviewOpen}
        onClose={handleElevationPreviewClose}
        elevationThumbnailPath={elevationPreviewRoute?.elevation_thumbnail_path || null}
        routeName={elevationPreviewRoute?.name || 'Route'}
      />
      
      {/* Download Modal */}
      <DownloadModal
        open={downloadModalOpen}
        title="Generating GPX File"
        message={`Calculating route "${selectedRoute?.name}" and generating GPX file...`}
        allowClose={false}
      />
      
      {/* GPX Upload Modal */}
      <GPXUploadModal
        open={gpxUploadModalOpen}
        onClose={() => setGpxUploadModalOpen(false)}
        onRouteUploaded={handleRouteUploaded}
      />
    </Container>
  );
};

export default MyRoutesPage;
